import React, { useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { HardDrive, Loader2, PlugZap, Save, CheckCircle2, Cloud, CloudCog, Database as DbIcon, Server, Zap, MoveRight, AlertCircle, StopCircle, Trash2 } from "lucide-react";

const PROVIDERS = [
  { value: "emergent",   label: "Emergent managed storage (zero config)", Icon: HardDrive },
  { value: "s3",         label: "Amazon S3 / S3-compatible (MinIO, Wasabi…)", Icon: Cloud },
  { value: "azure_blob", label: "Azure Blob Storage",                       Icon: CloudCog },
  { value: "gcs",        label: "Google Cloud Storage (GCS)",               Icon: DbIcon },
  { value: "oci_native", label: "Oracle OCI Object Storage (native API)",   Icon: Server },
  { value: "akamai",     label: "Akamai Cloud Object Storage (Linode)",     Icon: Zap },
];

const PROVIDER_LABEL = Object.fromEntries(PROVIDERS.map((p) => [p.value, p.label]));

export function StorageSettings() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [status, setStatus] = useState(null);        // migration status
  const [migrating, setMigrating] = useState(false); // sync migration in progress
  const [migrateReport, setMigrateReport] = useState(null);
  const [deleteSource, setDeleteSource] = useState(false);
  const [backgroundMode, setBackgroundMode] = useState(false);
  const [activeJob, setActiveJob] = useState(null);  // {id,status,total,processed,migrated,failed,...}
  const pollRef = useRef(null);

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/storage-settings/migration-status");
      setStatus(data);
    } catch { setStatus(null); }
  }, []);

  const loadActiveJob = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/storage-settings/migrate/jobs", { params: { limit: 1 } });
      const j = (data.items || [])[0];
      if (j && (j.status === "pending" || j.status === "running")) setActiveJob(j);
    } catch { /* noop */ }
  }, []);

  useEffect(() => {
    api.get("/admin/storage-settings")
      .then(({ data }) => setCfg({ provider: "emergent", s3: {}, azure: {}, gcs: {}, oci: {}, akamai: {}, ...data }))
      .catch(() => setCfg({ provider: "emergent", s3: {}, azure: {}, gcs: {}, oci: {}, akamai: {} }));
    loadStatus();
    loadActiveJob();
  }, [loadStatus, loadActiveJob]);

  // Poll the active job every 1.5s while running.
  useEffect(() => {
    if (!activeJob || (activeJob.status !== "pending" && activeJob.status !== "running")) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/admin/storage-settings/migrate/jobs/${activeJob.id}`);
        setActiveJob(data);
        if (data.status === "done" || data.status === "failed" || data.status === "cancelled") {
          clearInterval(pollRef.current); pollRef.current = null;
          if (data.status === "done") toast.success(`Migration complete: ${data.migrated || 0} moved, ${data.failed || 0} failed`);
          if (data.status === "failed") toast.error(`Migration failed: ${data.error || "unknown"}`);
          if (data.status === "cancelled") toast.info("Migration cancelled");
          loadStatus();
        }
      } catch { /* keep polling — transient */ }
    }, 1500);
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [activeJob, loadStatus]);

  if (!cfg) {
    return <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-[color:var(--brand-ocean)]" /></div>;
  }

  const setBlock = (key, patch) => setCfg((c) => ({ ...c, [key]: { ...(c[key] || {}), ...patch } }));

  const save = async () => {
    setSaving(true); setTestResult(null);
    try {
      await api.put("/admin/storage-settings", cfg);
      toast.success("Storage settings saved");
      loadStatus();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true); setTestResult(null);
    try {
      const { data } = await api.post("/admin/storage-settings/test");
      setTestResult({ ok: true, provider: data.provider });
      toast.success(`Storage healthy (${PROVIDER_LABEL[data.provider] || data.provider})`);
    } catch (e) {
      setTestResult({ ok: false, error: e?.response?.data?.detail || "Test failed" });
      toast.error(e?.response?.data?.detail || "Storage test failed");
    } finally { setTesting(false); }
  };

  const runSyncMigration = async (dryRun) => {
    setMigrating(true);
    const agg = { processed: 0, migrated: 0, failed: 0, source_deleted: 0, delete_warnings: 0, errors: [], dry_run: !!dryRun };
    try {
      // Loop in batches of 100 until backend reports 0 remaining OR no progress (all failing).
      for (let i = 0; i < 40; i++) { // hard-cap 40 * 100 = 4000 items per click
        const { data } = await api.post("/admin/storage-settings/migrate",
          null, { params: { dry_run: !!dryRun, limit: 100, delete_source: !!deleteSource } });
        agg.processed += data.processed;
        agg.migrated += data.migrated;
        agg.failed += data.failed;
        agg.source_deleted += data.source_deleted || 0;
        agg.delete_warnings += data.delete_warnings || 0;
        agg.errors = agg.errors.concat(data.errors || []).slice(0, 20);
        agg.active_provider = data.active_provider;
        // Stop when nothing is left, or nothing succeeded this round (all fetched items errored).
        if (!data.remaining || (!dryRun && data.migrated === 0)) break;
      }
      setMigrateReport(agg);
      if (dryRun) {
        toast.success(`Dry-run OK: ${agg.migrated} media ready to migrate, ${agg.failed} would fail`);
      } else {
        const bits = [`${agg.migrated} moved`];
        if (deleteSource) bits.push(`${agg.source_deleted} originals deleted`);
        if (agg.failed) bits.push(`${agg.failed} failed`);
        toast.success(`Migration: ${bits.join(" · ")}`);
      }
      loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Migration failed");
    } finally { setMigrating(false); }
  };

  const startBackgroundJob = async () => {
    try {
      const { data } = await api.post("/admin/storage-settings/migrate/start",
        null, { params: { delete_source: !!deleteSource } });
      toast.success("Background migration started");
      // Load the freshly-created job so polling starts immediately.
      const { data: job } = await api.get(`/admin/storage-settings/migrate/jobs/${data.job_id}`);
      setActiveJob(job);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start job"); }
  };

  const cancelJob = async () => {
    if (!activeJob) return;
    try {
      await api.post(`/admin/storage-settings/migrate/jobs/${activeJob.id}/cancel`);
      toast.info("Cancellation requested");
    } catch (e) { toast.error(e?.response?.data?.detail || "Cancel failed"); }
  };

  return (
    <div data-testid="storage-settings" className="glass-card p-5 max-w-2xl space-y-4">
      <div>
        <h3 className="font-display text-base text-[color:var(--ink)] flex items-center gap-2"><HardDrive className="w-4 h-4 text-[color:var(--brand-ocean)]" />Object storage</h3>
        <p className="text-xs text-[color:var(--ink-3)] mt-1">Where uploaded images (widget backgrounds, markers, wallpapers, icons) are stored for this tenant. Secrets are masked once saved.</p>
      </div>

      <div>
        <Label className="text-xs text-[color:var(--ink-3)]">Provider</Label>
        <Select value={cfg.provider || "emergent"} onValueChange={(v) => setCfg((c) => ({ ...c, provider: v }))}>
          <SelectTrigger data-testid="storage-provider" className="mt-1.5 bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
          <SelectContent>
            {PROVIDERS.map((p) => (
              <SelectItem key={p.value} value={p.value} data-testid={`storage-provider-opt-${p.value}`}>
                <span className="flex items-center gap-2"><p.Icon className="w-3.5 h-3.5" />{p.label}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {cfg.provider === "s3" && <S3Fields s3={cfg.s3 || {}} setS3={(patch) => setBlock("s3", patch)} />}
      {cfg.provider === "azure_blob" && <AzureFields az={cfg.azure || {}} setAz={(patch) => setBlock("azure", patch)} />}
      {cfg.provider === "gcs" && <GcsFields gc={cfg.gcs || {}} setGc={(patch) => setBlock("gcs", patch)} />}
      {cfg.provider === "oci_native" && <OciFields oc={cfg.oci || {}} setOc={(patch) => setBlock("oci", patch)} />}
      {cfg.provider === "akamai" && <AkamaiFields ak={cfg.akamai || {}} setAk={(patch) => setBlock("akamai", patch)} />}

      {status && (status.needs_migration || activeJob) && (
        <MigrationBanner
          status={status}
          job={activeJob}
          migrating={migrating}
          report={migrateReport}
          deleteSource={deleteSource}
          onDeleteSourceChange={setDeleteSource}
          backgroundMode={backgroundMode}
          onBackgroundModeChange={setBackgroundMode}
          onDryRun={() => runSyncMigration(true)}
          onMigrate={() => (backgroundMode ? startBackgroundJob() : runSyncMigration(false))}
          onCancel={cancelJob}
        />
      )}

      <StorageInsightsPanel />

      <div className="flex items-center gap-2">
        <Button data-testid="storage-save-btn" onClick={save} disabled={saving} className="bg-[color:var(--brand-ocean)] text-white">
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}Save
        </Button>
        <Button data-testid="storage-test-btn" variant="outline" onClick={test} disabled={testing} className="bg-[color:var(--surface)]">
          {testing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <PlugZap className="w-4 h-4 mr-1" />}Test connection
        </Button>
        {testResult?.ok && <span data-testid="storage-test-ok" className="text-xs text-[color:var(--success)] flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" />Working</span>}
        {testResult && !testResult.ok && <span data-testid="storage-test-fail" className="text-xs text-[color:var(--danger)]">{testResult.error}</span>}
      </div>
    </div>
  );
}

// ---- provider-specific field blocks ----

function Field({ label, hint, testid, children }) {
  return (
    <div>
      <Label className="text-xs text-[color:var(--ink-3)]">{label} {hint && <span className="opacity-70">{hint}</span>}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

const blockClass = "space-y-3 rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--surface-2)] p-3";

function S3Fields({ s3, setS3 }) {
  return (
    <div className={blockClass}>
      <Field label="Endpoint URL" hint="(blank for AWS S3; set for OCI / MinIO / Wasabi)">
        <Input data-testid="s3-endpoint" value={s3.endpoint_url || ""} onChange={(e) => setS3({ endpoint_url: e.target.value })} placeholder="https://s3.us-east-1.amazonaws.com" className="bg-[color:var(--surface)]" />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Region"><Input data-testid="s3-region" value={s3.region || ""} onChange={(e) => setS3({ region: e.target.value })} placeholder="us-east-1" className="bg-[color:var(--surface)]" /></Field>
        <Field label="Bucket *"><Input data-testid="s3-bucket" value={s3.bucket || ""} onChange={(e) => setS3({ bucket: e.target.value })} placeholder="my-bi-assets" className="bg-[color:var(--surface)]" /></Field>
      </div>
      <Field label="Access key *"><Input data-testid="s3-access-key" value={s3.access_key || ""} onChange={(e) => setS3({ access_key: e.target.value })} className="bg-[color:var(--surface)]" /></Field>
      <Field label="Secret key *">
        <Input data-testid="s3-secret-key" type="password" value={s3.secret_key || ""} onChange={(e) => setS3({ secret_key: e.target.value })} placeholder="********" className="bg-[color:var(--surface)]" />
        <p className="text-[10px] text-[color:var(--ink-3)] mt-1">Leave as ******** to keep the saved secret.</p>
      </Field>
    </div>
  );
}

function AzureFields({ az, setAz }) {
  return (
    <div className={blockClass}>
      <Field label="Container *"><Input data-testid="az-container" value={az.container || ""} onChange={(e) => setAz({ container: e.target.value })} placeholder="bi-media" className="bg-[color:var(--surface)]" /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Storage account name"><Input data-testid="az-account" value={az.account_name || ""} onChange={(e) => setAz({ account_name: e.target.value })} placeholder="mycompany" className="bg-[color:var(--surface)]" /></Field>
        <Field label="Account key"><Input data-testid="az-account-key" type="password" value={az.account_key || ""} onChange={(e) => setAz({ account_key: e.target.value })} placeholder="********" className="bg-[color:var(--surface)]" /></Field>
      </div>
      <Field label="…or full connection string" hint="(overrides the fields above)">
        <Textarea data-testid="az-conn-string" value={az.connection_string || ""} onChange={(e) => setAz({ connection_string: e.target.value })} placeholder="DefaultEndpointsProtocol=https;AccountName=…;AccountKey=…" className="min-h-[64px] bg-[color:var(--surface)] font-mono text-[11px]" />
      </Field>
      <p className="text-[10px] text-[color:var(--ink-3)]">Provide either (account_name + account_key) OR the full connection string. Keys are masked once saved.</p>
    </div>
  );
}

function GcsFields({ gc, setGc }) {
  return (
    <div className={blockClass}>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Project ID" hint="(optional, taken from JSON if blank)"><Input data-testid="gcs-project" value={gc.project || ""} onChange={(e) => setGc({ project: e.target.value })} placeholder="my-gcp-project" className="bg-[color:var(--surface)]" /></Field>
        <Field label="Bucket *"><Input data-testid="gcs-bucket" value={gc.bucket || ""} onChange={(e) => setGc({ bucket: e.target.value })} placeholder="my-bi-assets" className="bg-[color:var(--surface)]" /></Field>
      </div>
      <Field label="Service account JSON *" hint="(paste the whole JSON key file)">
        <Textarea data-testid="gcs-sa-json" value={gc.service_account_json || ""} onChange={(e) => setGc({ service_account_json: e.target.value })} placeholder='{ "type": "service_account", "project_id": "...", "private_key": "-----BEGIN PRIVATE KEY-----\\n..." }' className="min-h-[110px] bg-[color:var(--surface)] font-mono text-[11px]" />
      </Field>
      <p className="text-[10px] text-[color:var(--ink-3)]">Give the service account <span className="font-mono">Storage Object Admin</span> on the bucket. JSON is masked once saved.</p>
    </div>
  );
}

function OciFields({ oc, setOc }) {
  return (
    <div className={blockClass}>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Namespace *"><Input data-testid="oci-namespace" value={oc.namespace || ""} onChange={(e) => setOc({ namespace: e.target.value })} placeholder="axaxnpcrorw5" className="bg-[color:var(--surface)]" /></Field>
        <Field label="Bucket *"><Input data-testid="oci-bucket" value={oc.bucket || ""} onChange={(e) => setOc({ bucket: e.target.value })} placeholder="bi-media" className="bg-[color:var(--surface)]" /></Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Region *"><Input data-testid="oci-region" value={oc.region || ""} onChange={(e) => setOc({ region: e.target.value })} placeholder="us-ashburn-1" className="bg-[color:var(--surface)]" /></Field>
        <Field label="Fingerprint *"><Input data-testid="oci-fingerprint" value={oc.fingerprint || ""} onChange={(e) => setOc({ fingerprint: e.target.value })} placeholder="aa:bb:cc:…" className="bg-[color:var(--surface)] font-mono text-[11px]" /></Field>
      </div>
      <Field label="Tenancy OCID *"><Input data-testid="oci-tenancy" value={oc.tenancy_ocid || ""} onChange={(e) => setOc({ tenancy_ocid: e.target.value })} placeholder="ocid1.tenancy.oc1..…" className="bg-[color:var(--surface)] font-mono text-[11px]" /></Field>
      <Field label="User OCID *"><Input data-testid="oci-user" value={oc.user_ocid || ""} onChange={(e) => setOc({ user_ocid: e.target.value })} placeholder="ocid1.user.oc1..…" className="bg-[color:var(--surface)] font-mono text-[11px]" /></Field>
      <Field label="Private key (PEM) *">
        <Textarea data-testid="oci-private-key" value={oc.private_key || ""} onChange={(e) => setOc({ private_key: e.target.value })} placeholder={"-----BEGIN PRIVATE KEY-----\n…\n-----END PRIVATE KEY-----"} className="min-h-[120px] bg-[color:var(--surface)] font-mono text-[11px]" />
      </Field>
      <p className="text-[10px] text-[color:var(--ink-3)]">The key is masked once saved. Paste again to rotate.</p>
    </div>
  );
}

function AkamaiFields({ ak, setAk }) {
  return (
    <div className={blockClass}>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Cluster / Region *" hint="e.g. us-east-1, eu-central-1, us-mia-1">
          <Input data-testid="akamai-region" value={ak.region || ""} onChange={(e) => setAk({ region: e.target.value })} placeholder="us-east-1" className="bg-[color:var(--surface)]" />
        </Field>
        <Field label="Bucket *">
          <Input data-testid="akamai-bucket" value={ak.bucket || ""} onChange={(e) => setAk({ bucket: e.target.value })} placeholder="my-bi-assets" className="bg-[color:var(--surface)]" />
        </Field>
      </div>
      <Field label="Access key *">
        <Input data-testid="akamai-access-key" value={ak.access_key || ""} onChange={(e) => setAk({ access_key: e.target.value })} className="bg-[color:var(--surface)]" />
      </Field>
      <Field label="Secret key *">
        <Input data-testid="akamai-secret-key" type="password" value={ak.secret_key || ""} onChange={(e) => setAk({ secret_key: e.target.value })} placeholder="********" className="bg-[color:var(--surface)]" />
        <p className="text-[10px] text-[color:var(--ink-3)] mt-1">Leave as ******** to keep the saved secret.</p>
      </Field>
      <Field label="Endpoint URL" hint="(optional — auto-derived from region as https://<region>.linodeobjects.com)">
        <Input data-testid="akamai-endpoint" value={ak.endpoint_url || ""} onChange={(e) => setAk({ endpoint_url: e.target.value })} placeholder="https://us-east-1.linodeobjects.com" className="bg-[color:var(--surface)]" />
      </Field>
    </div>
  );
}

function MigrationBanner({ status, job, migrating, report, deleteSource, onDeleteSourceChange,
                           backgroundMode, onBackgroundModeChange, onDryRun, onMigrate, onCancel }) {
  const active = status.active_provider;
  const activeLabel = PROVIDER_LABEL[active] || active;
  const byProv = status.counts_by_provider || {};
  const others = Object.entries(byProv).filter(([k]) => k !== active);
  const jobRunning = job && (job.status === "pending" || job.status === "running");
  const jobPct = job && job.total ? Math.min(100, Math.round((job.processed / job.total) * 100)) : 0;
  const busy = migrating || jobRunning;
  return (
    <div data-testid="storage-migration-banner" className="rounded-xl border border-[color:var(--warning-border,rgba(251,146,60,0.35))] bg-[color:rgba(251,146,60,0.08)] p-3 space-y-2">
      <div className="flex items-start gap-2">
        <AlertCircle className="w-4 h-4 text-[color:var(--warning,#f97316)] mt-0.5 shrink-0" />
        <div className="text-xs text-[color:var(--ink-2)] flex-1">
          <div className="font-medium text-[color:var(--ink)]">
            {status.other_provider_count.toLocaleString()} media file{status.other_provider_count === 1 ? "" : "s"} still live on other providers.
          </div>
          <div className="mt-1">Current active provider: <span className="font-mono">{activeLabel}</span> ({(byProv[active] || 0).toLocaleString()} files). New uploads always land on the active provider, but existing files stay where they are unless you migrate.</div>
          <ul className="mt-1.5 space-y-0.5">
            {others.map(([prov, n]) => (
              <li key={prov} className="flex items-center gap-1.5 font-mono text-[11px] text-[color:var(--ink-3)]">
                <span className="inline-block w-3 h-3 rounded-sm bg-[color:var(--surface-2)] border border-[color:var(--glass-border)]" />
                {n.toLocaleString()} on <span className="text-[color:var(--ink-2)]">{PROVIDER_LABEL[prov] || prov}</span>
                <MoveRight className="w-3 h-3 mx-0.5" />
                {activeLabel}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Migration options */}
      <div className="flex flex-wrap items-center gap-4 pl-6 pt-1">
        <label className="flex items-center gap-2 text-xs text-[color:var(--ink-2)] cursor-pointer">
          <Switch data-testid="storage-migrate-delete-source" checked={deleteSource} onCheckedChange={onDeleteSourceChange} disabled={busy} />
          <Trash2 className="w-3.5 h-3.5 text-[color:var(--danger)]" />
          <span>Delete originals after successful copy</span>
        </label>
        <label className="flex items-center gap-2 text-xs text-[color:var(--ink-2)] cursor-pointer">
          <Switch data-testid="storage-migrate-background" checked={backgroundMode} onCheckedChange={onBackgroundModeChange} disabled={busy} />
          <span>Run as background job (for large tenants)</span>
        </label>
      </div>

      {/* Live job progress */}
      {job && (
        <div data-testid="storage-migrate-job-progress" className="rounded-lg bg-[color:var(--surface)] p-2.5 space-y-1.5 border border-[color:var(--glass-border)]">
          <div className="flex items-center justify-between text-[11px]">
            <div className="flex items-center gap-1.5 text-[color:var(--ink-2)]">
              <span className="font-mono">{job.id.slice(0, 8)}</span>
              <span className="text-[color:var(--ink-3)]">·</span>
              <span className={"px-1.5 py-0.5 rounded font-medium " + (
                job.status === "running" ? "bg-[color:var(--brand-ocean)] text-white" :
                job.status === "done" ? "bg-[color:var(--success)] text-white" :
                job.status === "failed" ? "bg-[color:var(--danger)] text-white" :
                job.status === "cancelled" ? "bg-[color:var(--ink-3)] text-white" :
                "bg-[color:var(--surface-2)] text-[color:var(--ink-2)]"
              )}>{job.status}</span>
              <span className="text-[color:var(--ink-3)]">·</span>
              <span>{(job.processed || 0).toLocaleString()} / {(job.total || 0).toLocaleString()} · {(job.migrated || 0).toLocaleString()} moved · {(job.failed || 0).toLocaleString()} failed{job.delete_source ? ` · ${(job.source_deleted || 0).toLocaleString()} originals removed` : ""}</span>
            </div>
            {jobRunning && (
              <Button data-testid="storage-migrate-cancel" size="sm" variant="outline" onClick={onCancel} className="h-7 bg-[color:var(--surface)]">
                <StopCircle className="w-3.5 h-3.5 mr-1" />Cancel
              </Button>
            )}
          </div>
          <Progress value={jobPct} className="h-1.5" />
        </div>
      )}

      {/* Primary actions */}
      <div className="flex flex-wrap items-center gap-2 pt-1">
        <Button data-testid="storage-migrate-dry-run" size="sm" variant="outline" onClick={onDryRun} disabled={busy} className="bg-[color:var(--surface)]">
          {migrating ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}Preview (dry-run)
        </Button>
        <Button data-testid="storage-migrate-run" size="sm" onClick={onMigrate} disabled={busy || jobRunning} className="bg-[color:var(--warning,#f97316)] hover:opacity-90 text-white">
          {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <MoveRight className="w-3.5 h-3.5 mr-1" />}
          {backgroundMode ? "Start background migration" : "Migrate now"}
        </Button>
        {report && !job && (
          <div data-testid="storage-migrate-report" className="text-[11px] text-[color:var(--ink-3)]">
            {report.dry_run ? "Preview" : "Result"}: {report.migrated.toLocaleString()} migrated · {report.failed.toLocaleString()} failed
            {deleteSource && !report.dry_run ? ` · ${(report.source_deleted || 0).toLocaleString()} originals deleted` : ""}
            {report.errors?.length > 0 && (
              <div className="mt-1 max-h-24 overflow-y-auto space-y-0.5">
                {report.errors.map((er, i) => (
                  <div key={i} className="font-mono text-[10px] text-[color:var(--danger)]">• {er.filename || er.media_id}: {er.error}</div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


function StorageInsightsPanel() {
  const [data, setData] = React.useState(null);
  const [pricing, setPricing] = React.useState(null);
  const [busy, setBusy] = React.useState(true);
  const [editing, setEditing] = React.useState(false);
  const [drafts, setDrafts] = React.useState({});
  const [currency, setCurrency] = React.useState("USD");
  const [rates, setRates] = React.useState({ USD: 1 });
  const [migrating, setMigrating] = React.useState(false);
  const load = React.useCallback(() => {
    setBusy(true);
    Promise.all([
      api.get("/admin/storage-settings/insights").then((r) => r.data).catch(() => null),
      api.get("/admin/storage-settings/pricing").then((r) => r.data).catch(() => null),
      api.get("/tenants/me").then((r) => r.data).then((me) => api.get(`/tenants/${me.id}/currency`)).then((r) => r.data).catch(() => null),
    ]).then(([ins, pr, cur]) => {
      setData(ins || { by_provider: [], total_bytes: 0, total_count: 0, total_est_monthly_usd: 0 });
      setPricing(pr);
      setDrafts(pr?.overrides || {});
      if (cur) { setCurrency(cur.currency || "USD"); setRates(cur.rates || { USD: 1 }); }
    }).finally(() => setBusy(false));
  }, []);
  React.useEffect(() => { load(); }, [load]);
  const savePricing = async () => {
    const clean = {};
    for (const [k, v] of Object.entries(drafts || {})) {
      const n = parseFloat(v);
      if (!isNaN(n) && n >= 0) clean[k] = n;
    }
    try {
      const { data: pr } = await api.put("/admin/storage-settings/pricing", { pricing: clean });
      setPricing(pr); setDrafts(pr.overrides || {}); setEditing(false); load();
      toast.success("Pricing saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const changeCurrency = async (cur) => {
    setCurrency(cur);
    try {
      const me = await api.get("/tenants/me").then((r) => r.data);
      await api.put(`/tenants/${me.id}/currency`, { currency: cur });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const runMigrate = async () => {
    setMigrating(true);
    try {
      await api.post("/admin/storage-settings/migrate/start", null, { params: { delete_source: false, dry_run: false, limit: 100 } });
      toast.success("Background migration started — savings apply as files move.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to start migration"); }
    finally { setMigrating(false); }
  };
  if (!data) return null;
  const cSym = { USD: "$", EUR: "€", GBP: "£", INR: "₹" };
  const cRate = rates[currency] || 1;
  const conv = (usd) => (usd || 0) * cRate;
  const fmt$ = (usd, decimals = 2) => `${cSym[currency] || "$"}${conv(usd).toFixed(decimals)}`;
  const fmtBytes = (b) => {
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
    return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
  };
  const editable = PROVIDERS.filter((p) => p.value !== "emergent");
  const savings = data.estimated_savings_usd || 0;
  return (
    <div data-testid="storage-insights" className={blockClass + " !p-4"}>
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-display text-sm text-[color:var(--ink)]">Storage insights</h3>
          <p className="text-[10px] text-[color:var(--ink-3)]">Bytes stored, file counts, and an estimated monthly cost per provider.</p>
        </div>
        <div className="flex items-center gap-2">
          <select data-testid="storage-currency" value={currency} onChange={(e) => changeCurrency(e.target.value)}
            className="h-7 rounded-md border border-[color:var(--glass-border)] bg-[color:var(--surface)] px-2 text-[11px]">
            {Object.keys(rates).map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <Button data-testid="storage-insights-edit-pricing" size="sm" variant="outline" onClick={() => setEditing((v) => !v)} className="h-7 text-[11px] bg-[color:var(--surface)]">
            {editing ? "Cancel" : "Edit rates"}
          </Button>
          <Button data-testid="storage-insights-refresh" size="sm" variant="outline" onClick={load} disabled={busy} className="h-7 text-[11px] bg-[color:var(--surface)]">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Refresh"}
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3 mt-3">
        <div className="p-2.5 rounded-lg bg-[color:var(--surface)] border border-[color:var(--glass-border)]" data-testid="storage-insights-total-files">
          <div className="text-[10px] text-[color:var(--ink-3)] uppercase tracking-wide">Total files</div>
          <div className="text-lg font-bold text-[color:var(--ink)]">{(data.total_count || 0).toLocaleString()}</div>
        </div>
        <div className="p-2.5 rounded-lg bg-[color:var(--surface)] border border-[color:var(--glass-border)]" data-testid="storage-insights-total-bytes">
          <div className="text-[10px] text-[color:var(--ink-3)] uppercase tracking-wide">Total size</div>
          <div className="text-lg font-bold text-[color:var(--ink)]">{fmtBytes(data.total_bytes || 0)}</div>
        </div>
        <div className="p-2.5 rounded-lg bg-[color:var(--surface)] border border-[color:var(--glass-border)]" data-testid="storage-insights-total-cost">
          <div className="text-[10px] text-[color:var(--ink-3)] uppercase tracking-wide">Est. monthly cost</div>
          <div className="text-lg font-bold text-[color:var(--brand-ocean)]">{fmt$(data.total_est_monthly_usd || 0)}</div>
        </div>
      </div>
      {data.by_provider?.length > 0 && (
        <div className="mt-3 space-y-1.5" data-testid="storage-insights-providers">
          {data.by_provider.map((p) => {
            const label = PROVIDER_LABEL[p.provider] || p.provider;
            const isActive = p.provider === data.active_provider;
            const pct = data.total_bytes ? Math.round((p.bytes / data.total_bytes) * 100) : 0;
            return (
              <div key={p.provider} className="flex items-center gap-3 text-[11px]" data-testid={`storage-insights-row-${p.provider}`}>
                <span className={`inline-block w-2 h-2 rounded-full ${isActive ? "bg-[color:var(--brand-ocean)]" : "bg-[color:var(--ink-3)]"}`} />
                <span className={`min-w-[130px] ${isActive ? "font-semibold text-[color:var(--ink)]" : "text-[color:var(--ink-2)]"}`}>{label}{isActive && <span className="ml-1 text-[9px] uppercase text-[color:var(--brand-ocean)]">active</span>}</span>
                <div className="flex-1 h-1.5 rounded-full bg-[color:var(--surface)] overflow-hidden">
                  <div className="h-full bg-[color:var(--brand-ocean)]" style={{ width: `${pct}%` }} />
                </div>
                <span className="text-[color:var(--ink-3)] tabular-nums">{p.count.toLocaleString()} files</span>
                <span className="text-[color:var(--ink-3)] tabular-nums min-w-[70px] text-right">{fmtBytes(p.bytes)}</span>
                <span className="text-[color:var(--ink)] tabular-nums min-w-[60px] text-right">{fmt$(p.est_monthly_usd || 0)}/mo</span>
              </div>
            );
          })}
        </div>
      )}
      {data.nudge && (
        <div data-testid="storage-insights-nudge" className="mt-3 p-2 rounded-lg bg-[color:var(--warning,#f97316)]/10 border border-[color:var(--warning,#f97316)]/30 text-[11px] text-[color:var(--ink)] flex items-start gap-2">
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 text-[color:var(--warning,#f97316)] flex-shrink-0" />
          <div className="flex-1">
            <div>{data.nudge}</div>
            {savings > 0 && (
              <Button data-testid="storage-insights-migrate-btn" size="sm" onClick={runMigrate} disabled={migrating}
                className="mt-2 h-7 text-[11px] bg-[color:var(--brand-ocean)] text-white">
                {migrating ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <MoveRight className="w-3.5 h-3.5 mr-1" />}
                Migrate &amp; save ≈ {fmt$(savings)}/mo
              </Button>
            )}
          </div>
        </div>
      )}

      {editing && pricing && (
        <div data-testid="storage-pricing-editor" className="mt-3 p-3 rounded-lg bg-[color:var(--surface)] border border-[color:var(--glass-border)] space-y-2">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-xs font-semibold text-[color:var(--ink)]">Provider cost overrides</h4>
              <p className="text-[10px] text-[color:var(--ink-3)]">Override the default $/GB-month per provider. Leave blank to use the default rate.</p>
            </div>
            <Button data-testid="storage-pricing-save" size="sm" onClick={savePricing} className="h-7 text-[11px] bg-[color:var(--brand-ocean)] text-white">Save rates</Button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {editable.map((p) => (
              <div key={p.value} className="flex items-center gap-1.5 text-[11px]">
                <span className="min-w-[80px] text-[color:var(--ink-2)] truncate">{p.value}</span>
                <span className="text-[color:var(--ink-3)]">$</span>
                <Input data-testid={`storage-pricing-input-${p.value}`} value={drafts[p.value] ?? ""}
                  onChange={(e) => setDrafts({ ...drafts, [p.value]: e.target.value })}
                  placeholder={String(pricing.defaults[p.value] ?? 0)}
                  className="h-7 text-[11px] bg-[color:var(--surface-2)]" />
                <span className="text-[9px] text-[color:var(--ink-3)] whitespace-nowrap">/GB·mo</span>
              </div>
            ))}
          </div>
          <p className="text-[9px] text-[color:var(--ink-3)]">Defaults are always in USD. Currency conversion applies only to the display above.</p>
        </div>
      )}
    </div>
  );
}
